/**
 * Opening a camera and a microphone, and saying precisely what went wrong when one will not open.
 *
 * ## Two requests, never one
 *
 * `getUserMedia({ video, audio })` is a single request for two devices and it fails **as a
 * whole**: a laptop whose microphone is disabled in the operating system's privacy settings gets
 * `NotFoundError` for the *pair*, no permission prompt at all, and no indication of which half was
 * missing. Asking for both at once leaves a host with two disabled pickers, one useless error,
 * and nothing to press.
 *
 * So the camera and the microphone are opened **independently**. One missing device costs only
 * itself, which is also what `R-VID-7` implies — *audio is more important than video* is not a
 * sentence you can honour if a missing camera takes the microphone with it.
 *
 * ## Enumerating before permission
 *
 * `enumerateDevices()` answers before any permission is granted, with the labels and ids blanked.
 * That is enough to know *whether* a machine has a camera at all, which is the difference between
 * "allow the camera" and "this machine has no camera" — two problems with nothing in common. The
 * real labels arrive on the second enumeration, after permission.
 */

/** Why a device would not open. Each one is a different sentence to a host, and a different fix. */
export type DeviceFailure =
  /** Permission refused — now, or once before and remembered by the browser. */
  | "blocked"
  /** No such device. Unplugged, disabled in the operating system, or never there. */
  | "missing"
  /** Present and held by something else, or refusing to start. */
  | "in-use"
  /** Present, and cannot do what was asked of it. Should not happen — see `cameraConstraints`. */
  | "unusable"
  /** No `mediaDevices` at all: an old browser, or a panel served over plain HTTP. */
  | "unsupported"
  | "unknown";

/**
 * A `getUserMedia` rejection, classified.
 *
 * The names are the ones browsers actually throw, including the two legacy Chrome spellings that
 * are still emitted by some builds. Pure, so `apps/host/test/devices.test.ts` can pin the mapping:
 * getting `missing` and `blocked` the wrong way round sends a host to the browser's permission
 * settings to fix a webcam that is switched off at the hardware level.
 */
export function classify(error: unknown): DeviceFailure {
  const name = error instanceof DOMException ? error.name : "";
  switch (name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
    case "SecurityError":
      return "blocked";
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "missing";
    case "NotReadableError":
    case "TrackStartError":
    case "AbortError":
      return "in-use";
    case "OverconstrainedError":
    case "ConstraintNotSatisfiedError":
      return "unusable";
    default:
      return "unknown";
  }
}

export type Opened<T> = { ok: true; track: MediaStreamTrack } | { ok: false; why: T };

/**
 * The camera's constraints (`R-VID-8`, spec §12.6).
 *
 * ## Ask for the device's own orientation, never for portrait unconditionally
 *
 * Asking for `720 × 1280` on every device produces **360 × 640** on a laptop — a quarter of the
 * pixels the camera can manage. The mechanism is worth writing down because it is
 * counter-intuitive and silent: a browser picks a camera mode by **fitness distance**, summing
 * `|actual − ideal| / max(actual, ideal)` per dimension, and against an ideal of `720 × 1280` a
 * `640 × 480` mode scores *better* than `1280 × 720`:
 *
 * ```
 *    640 × 480   width 0.111 + height 0.625 = 0.736   ← chosen
 *   1280 × 720   width 0.438 + height 0.438 = 0.875
 * ```
 *
 * It then crops and scales that to the portrait shape asked for, and the host publishes a soft
 * 360 × 640 picture while their preview looks fine. **An `ideal` that does not match the hardware
 * does not degrade gracefully towards the best available — it degrades towards the arithmetic.**
 *
 * So the orientation comes from the device. A laptop is landscape and gets `1280 × 720`, which is
 * both its native mode and the ceiling IVS real-time publishing allows (spec §12.6), so the fitness
 * distance is zero and nothing is cropped. A phone held upright asks for `720 × 1280` — and on
 * Chrome for Android that ask is read the other way round, which `deliveredSideways` corrects.
 *
 * Still `ideal` rather than `exact`: an exact constraint a camera cannot satisfy fails the whole
 * request instead of giving what it has, and refusing to open a camera at all is much worse than
 * opening it at 480p.
 */
function cameraConstraints(deviceId?: string): MediaTrackConstraints {
  /**
   * **The back camera, where there is one** (spec §17.3).
   *
   * The host is presenting *products*, and the front camera points at their face. On a phone
   * `environment` is the better lens by a wide margin and the one aimed at the thing being sold;
   * on a laptop there is no such camera and an `ideal` is ignored rather than failing.
   *
   * Omitted entirely when a device has been chosen explicitly: `deviceId: { exact: … }` already
   * names the camera, and asking for a facing mode alongside it is at best redundant and at worst
   * a constraint the named device cannot satisfy.
   */
  const facing: MediaTrackConstraints = deviceId
    ? {}
    : { facingMode: { ideal: "environment" } };

  return {
    ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
    ...facing,
    ...cameraSize(heldUpright()),
    frameRate: { ideal: 30 },
  };
}

/**
 * The viewport's orientation, which on a phone is how it is being held and on a laptop is
 * landscape and stays that way. `matchMedia` rather than `screen.orientation`, which is absent in
 * enough places to need a fallback anyway.
 */
function heldUpright(): boolean {
  return typeof window.matchMedia === "function"
    ? window.matchMedia("(orientation: portrait)").matches
    : false;
}

/** `720 × 1280` for a portrait picture, `1280 × 720` for a landscape one — as `ideal`s. */
export function cameraSize(
  portrait: boolean,
): Pick<MediaTrackConstraints, "width" | "height"> {
  const long = 1280;
  const short = 720;
  return {
    width: { ideal: portrait ? short : long },
    height: { ideal: portrait ? long : short },
  };
}

/**
 * Whether the camera delivered the other orientation from the one the device is held in.
 *
 * ## Why this is asked after the camera opens rather than settled before it
 *
 * **Browsers do not agree on which way round `width` and `height` are.** Safari reads them in the
 * picture's terms — ask for `720 × 1280` while holding the phone upright and you get a portrait
 * picture. Chrome on Android reads them in the **sensor's**, which is landscape however the phone
 * is held, and rotates the frames for the display afterwards. On a Pixel 4 in portrait, measured
 * over adb on 2026-09-29 (Chrome 101):
 *
 * ```
 *   asked 720 × 1280   →   delivered 1280 × 720, crop-and-scale   ← what the host saw
 *   asked 1280 × 720   →   delivered 720 × 1280                   ← what they wanted
 * ```
 *
 * The first is Chrome cutting a tall strip out of the landscape sensor to satisfy a "portrait"
 * ask, then rotating it: a wide picture of a narrow slice of the scene, front and back camera
 * alike. The host saw a horizontal, heavily cropped image from a phone held upright.
 *
 * Detecting the browser to decide which way to ask would be a guess about a behaviour nobody
 * documents and that could change in any release. So the camera is asked the obvious way, the
 * answer is **read back**, and a picture the wrong way round is asked for once more with the two
 * numbers swapped (`applyConstraints` changes an open track in place — measured on the same phone).
 * That is right on every browser, including the ones not yet tested.
 *
 * A square or unknown answer is left alone: neither way round is closer, and swapping would be
 * change for its own sake.
 */
export function deliveredSideways(
  settings: Pick<MediaTrackSettings, "width" | "height">,
  portrait: boolean,
): boolean {
  const { width, height } = settings;
  if (!width || !height || width === height) return false;
  return height > width !== portrait;
}

/**
 * Turns a camera that opened the wrong way round the right way round, once. Never throws: a
 * refused `applyConstraints` leaves the track as it opened, which is a picture — and a picture the
 * wrong shape is far better than no camera at all.
 */
async function straighten(track: MediaStreamTrack): Promise<void> {
  const portrait = heldUpright();
  if (!deliveredSideways(track.getSettings(), portrait)) return;
  try {
    await track.applyConstraints({
      ...track.getConstraints(),
      ...cameraSize(!portrait),
    });
  } catch {
    // Kept as it opened. See above.
  }
}

/**
 * Whether a camera track is pointing at the person holding the device.
 *
 * Decides whether the preview is mirrored. A **self view** must be mirrored — every video call has
 * trained that reflex, and aiming a camera at yourself in an unmirrored preview is unexpectedly
 * hard. A **back camera** must not be: it is pointing at a product on a table, and a mirrored view
 * of that is simply wrong, with text on packaging backwards.
 *
 * Desktop webcams generally report no `facingMode` at all, and they are self views — so the
 * default when nothing is known is to mirror, and only a camera that says `environment` is left
 * alone.
 */
export function isSelfView(track: MediaStreamTrack | undefined): boolean {
  if (!track) return true;
  return track.getSettings().facingMode !== "environment";
}

/**
 * The microphone's.
 *
 * Echo cancellation, noise suppression and gain control are left **on**, deliberately: a host is
 * talking in a room rather than recording music, and those three are what make a laptop
 * microphone tolerable for the thing `R-VID-7` says matters most.
 */
function microphoneConstraints(deviceId?: string): MediaTrackConstraints {
  return deviceId ? { deviceId: { exact: deviceId } } : {};
}

async function openTrack(
  constraints: MediaStreamConstraints,
  kind: "video" | "audio",
  deviceId: string | undefined,
): Promise<Opened<DeviceFailure>> {
  if (typeof navigator.mediaDevices?.getUserMedia !== "function") {
    return { ok: false, why: "unsupported" };
  }
  try {
    const media = await navigator.mediaDevices.getUserMedia(constraints);
    const track =
      kind === "video" ? media.getVideoTracks()[0] : media.getAudioTracks()[0];
    if (!track) {
      // A stream with no track of the kind we asked for. Not a documented outcome, and treating
      // it as success would hand a `undefined` track to the publisher.
      media.getTracks().forEach((one) => one.stop());
      return { ok: false, why: "missing" };
    }
    return { ok: true, track };
  } catch (error) {
    const why = classify(error);
    /**
     * One retry, and only for this case: a device that cannot satisfy an **exact** `deviceId` is
     * usually one that has been unplugged since the picker was filled in, and asking again without
     * it gets the host a working default rather than a dead end.
     */
    if (why === "unusable" && deviceId) {
      return openTrack(
        kind === "video"
          ? { video: cameraConstraints() }
          : { audio: microphoneConstraints() },
        kind,
        undefined,
      );
    }
    return { ok: false, why };
  }
}

export async function openCamera(deviceId?: string): Promise<Opened<DeviceFailure>> {
  const opened = await openTrack(
    { video: cameraConstraints(deviceId) },
    "video",
    deviceId,
  );
  if (opened.ok) await straighten(opened.track);
  return opened;
}

export function openMicrophone(deviceId?: string): Promise<Opened<DeviceFailure>> {
  return openTrack({ audio: microphoneConstraints(deviceId) }, "audio", deviceId);
}

export interface DeviceLists {
  cameras: MediaDeviceInfo[];
  microphones: MediaDeviceInfo[];
}

/**
 * What this machine has.
 *
 * Safe to call before permission — the entries come back with blank labels and ids, which is
 * useless for choosing and exactly right for telling a host that their laptop has no camera
 * rather than that they have not allowed one. Devices with no `deviceId` are kept in the count and
 * cannot be selected, which is why `Broadcast.tsx` filters them out of the pickers.
 */
export async function listDevices(): Promise<DeviceLists> {
  if (typeof navigator.mediaDevices?.enumerateDevices !== "function") {
    return { cameras: [], microphones: [] };
  }
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return {
      cameras: devices.filter((one) => one.kind === "videoinput"),
      microphones: devices.filter((one) => one.kind === "audioinput"),
    };
  } catch {
    return { cameras: [], microphones: [] };
  }
}

/**
 * Whether this browser has **already** granted the camera and the microphone.
 *
 * Chromium remembers a permission per origin, so a host who allowed these last week should not
 * have to press anything this week — `usePublisher` opens the devices on mount when this answers
 * true, and asks for nothing when it does not. That is the whole reason it exists: it separates
 * *we can turn the camera on without interrupting anybody* from *pressing the button will show a
 * prompt*, and only the second is worth making a host choose.
 *
 * `camera` and `microphone` are not in the Permissions API's standard registry, so a browser is
 * entitled to reject the query outright. Anything other than a clear "granted" for both is treated
 * as no, which errs towards prompting rather than towards opening a camera nobody asked for.
 */
export async function alreadyAllowed(): Promise<boolean> {
  if (typeof navigator.permissions?.query !== "function") return false;
  try {
    const states = await Promise.all(
      ["camera", "microphone"].map((name) =>
        navigator.permissions.query({ name: name as PermissionName }),
      ),
    );
    return states.every((state) => state.state === "granted");
  } catch {
    return false;
  }
}
